import { useEffect, useMemo, useRef } from 'preact/hooks';
import { useQuery } from '@tanstack/react-query';
import { getPortalDetailRoadDiagram, getPortalDetailSection } from '../../lib/portal-detail-api.js';
import { setupPortalRoadSvgPanZoom } from '../../lib/portal-road-svg-pan-zoom.js';
import { DetailTabSkeleton } from './DetailTabSkeleton.jsx';
import TerrainBandBackToMapButton from './TerrainBandBackToMapButton.jsx';

function formatCount(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(n);
}

function formatMeters(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(n)} m`;
}

function normalizeRoadName(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  const lowered = text.toLowerCase();
  if (lowered === 'nan' || lowered === 'none' || lowered === 'null') return null;
  return text;
}

function hasFiniteLength(value) {
  const n = Number(value);
  return Number.isFinite(n);
}

function RoadHeroNote({ lengthM, defaultNote, roadName }) {
  const name = normalizeRoadName(roadName);
  return (
    <div className="pp-detail-road-corner-note">
      {defaultNote}
      {hasFiniteLength(lengthM) && name ? (
        <span className="pp-detail-road-corner-road-name">{name}</span>
      ) : null}
    </div>
  );
}

function RoadSvgPanel({ svg, isLoading, error, isRoadV2 }) {
  const panRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!svg || !panRef.current) return undefined;

    panRef.current.innerHTML = svg;
    const svgEl = panRef.current.querySelector('svg');
    if (svgEl) {
      cleanupRef.current = setupPortalRoadSvgPanZoom(panRef.current, svgEl);
    }
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [svg]);

  if (!isRoadV2) {
    return (
      <div className="pp-detail-road-svg-card">
        <p className="pp-detail-empty">Bu sorgu RoadV2 verisi içermiyor.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="pp-detail-road-svg-card">
        <DetailTabSkeleton variant="card" label="Yol diyagramı yükleniyor…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pp-detail-road-svg-card">
        <p className="pp-detail-empty">{error.message || 'Yol diyagramı yüklenemedi.'}</p>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="pp-detail-road-svg-card">
        <p className="pp-detail-empty">Kırpılmamış yol + cephe SVG yok.</p>
      </div>
    );
  }

  return (
    <div className="pp-detail-road-svg-card">
      <p className="pp-detail-road-svg-lead">
        Sarı kaba temizlik alanları ve dış box olmadan: kırmızı parsel, turuncu yollar,
        mavi cephe çizgileri, cephe uzunluğu, yol isimleri ve kırmızı bağlantı noktaları.
      </p>
      <div
        ref={panRef}
        className="pp-detail-road-svg-pan"
        role="img"
        aria-label="Kırpılmamış yol ve cephe diyagramı"
      />
    </div>
  );
}

/**
 * Terrain band harita alanında yol SVG + metrik paneli.
 */
export default function TerrainBandRoadPanelOverlay({
  open = false,
  snapshotId = null,
  onClose,
}) {
  const enabled = Boolean(open && snapshotId);

  const metricsQuery = useQuery({
    queryKey: ['portal-detail-section', snapshotId, 'road'],
    queryFn: () => getPortalDetailSection(snapshotId, 'road'),
    enabled,
    staleTime: 120_000,
  });

  const diagramQuery = useQuery({
    queryKey: ['portal-detail-road-diagram', snapshotId],
    queryFn: () => getPortalDetailRoadDiagram(snapshotId),
    enabled: enabled && Boolean(metricsQuery.data?.road_metrics?.is_road_v2),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const metrics = metricsQuery.data?.road_metrics || {};
  const isRoadV2 = Boolean(metrics.is_road_v2);

  const metricCards = useMemo(() => ([
    { label: 'Bağlantılı', value: formatCount(metrics.connection_count) },
    { label: 'Varsayılan bağlantılı', value: formatCount(metrics.assumed_connection_count) },
    { label: 'Sadece cephe', value: formatCount(metrics.frontage_only_count) },
    { label: 'Toplam cephe uzunluğu', value: formatMeters(metrics.total_frontage_length_m) },
  ]), [
    metrics.assumed_connection_count,
    metrics.connection_count,
    metrics.frontage_only_count,
    metrics.total_frontage_length_m,
  ]);

  if (!open) return null;

  return (
    <div
      className="pp-detail-terrain-band__mesh-expand pp-detail-terrain-band__road-panel"
      role="dialog"
      aria-modal="true"
      aria-label="yol analizi"
    >
      <div className="pp-detail-terrain-band__mesh-expand-bar">
        <span className="pp-detail-terrain-band__mesh-expand-title">yol analizi</span>
        <div className="pp-detail-terrain-band__mesh-expand-actions">
          <TerrainBandBackToMapButton onClick={onClose} />
        </div>
      </div>
      <div className="pp-detail-terrain-band__mesh-expand-body pp-detail-terrain-band__road-panel-body">
        {metricsQuery.isLoading ? (
          <DetailTabSkeleton variant="card" label="Yol verileri yükleniyor…" />
        ) : null}
        {metricsQuery.error ? (
          <div className="pp-detail-empty">{metricsQuery.error.message}</div>
        ) : null}
        {metricsQuery.data ? (
          <div className="pp-detail-tab-stack pp-detail-road-stack">
            <div className="pp-detail-road-hero-grid">
              <div className="pp-detail-road-corner-block">
                <div className="pp-detail-road-corner-label">Köşe Sayısı</div>
                <div className="pp-detail-road-corner-count">
                  {formatCount(metrics.corner_count)}
                </div>
                <div className="pp-detail-road-corner-note">
                  {metrics.corner_note || 'Gerçek bağlantılı parçalardan benzersiz etiket çiftleri'}
                </div>
              </div>
              <div className="pp-detail-road-corner-block">
                <div className="pp-detail-road-corner-label">Önemli Cephe</div>
                <div className="pp-detail-road-corner-count">
                  {formatMeters(metrics.important_frontage_length_m)}
                </div>
                <RoadHeroNote
                  lengthM={metrics.important_frontage_length_m}
                  defaultNote="En küçük yol indeksli bağlantı cephesi"
                  roadName={metrics.important_frontage_road_name}
                />
              </div>
              <div className="pp-detail-road-corner-block">
                <div className="pp-detail-road-corner-label">Uzun Cephe</div>
                <div className="pp-detail-road-corner-count">
                  {formatMeters(metrics.longest_frontage_length_m)}
                </div>
                <RoadHeroNote
                  lengthM={metrics.longest_frontage_length_m}
                  defaultNote="Ölçülen segmentler arasındaki en uzun cephe"
                  roadName={metrics.longest_frontage_road_name}
                />
              </div>
            </div>

            <div className="pp-detail-metrics pp-detail-highlight-grid--compact pp-detail-road-metrics">
              {metricCards.map((item) => (
                <div className="pp-detail-metric-card" key={item.label}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>

            <RoadSvgPanel
              svg={diagramQuery.data?.svg}
              isLoading={diagramQuery.isLoading}
              error={diagramQuery.error}
              isRoadV2={isRoadV2}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
